import { Component, lazy, Suspense, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NavHud } from "./camera/NavHud.js";
import { navStore, useNavSelector } from "./camera/nav.js";
import { layoutStore } from "./layout.js";
import { LoadingScreen } from "./LoadingScreen.js";
import { useWorldT } from "./i18n.js";
import { useWorldState, worldStore } from "./store.js";
import { VehiclesUi } from "./vehicles/VehiclesUi.js";
import type { WorldStore } from "./store.js";
import "./world.css";

// three.js stays out of the main bundle: the scene chunk loads only on the Dünya page.
const WorldScene = lazy(() => import("./WorldScene.js"));

/** True when a WebGL context can be created (false in old browsers, some headless runners). */
export function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

function Fallback() {
  const t = useWorldT();
  return (
    <div className="world-fallback" role="note" data-testid="world-fallback">
      <h2>{t("fallback.title")}</h2>
      <p>{t("fallback.text")}</p>
    </div>
  );
}

/** Turns a failed WebGL context or a scene crash into the friendly fallback. */
class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(): void {
    // The fallback is the whole response; a 3D failure must never take the app down.
  }
  override render() {
    return this.state.failed ? <Fallback /> : this.props.children;
  }
}

function Overlay({ store }: { store: WorldStore }) {
  const t = useWorldT();
  const { status } = useWorldState(store);
  if (status === "loading") {
    return (
      <p className="world-overlay" role="status">
        {t("state.loading")}
      </p>
    );
  }
  if (status === "unavailable") {
    return (
      <div className="world-overlay" role="alert">
        <p>{t("state.unavailable")}</p>
        <button type="button" onClick={() => store.retry()}>
          {t("state.retry")}
        </button>
      </div>
    );
  }
  return null;
}

/**
 * The 3D viewport: the live scene (terrain, parcels, the hub landmark, company buildings). The
 * frame loop is on-demand (an idle page renders nothing). Falls back to a notice without WebGL.
 */
export function WorldCanvas({ store = worldStore }: { store?: WorldStore }) {
  const [webgl, setWebgl] = useState<boolean | null>(null);
  // Double-click a building to go in: the selection the first click made counts even if the camera
  // flight moved the building from under the cursor before the second click.
  const recent = useRef<{ id: string; at: number } | null>(null);
  useEffect(
    () =>
      store.subscribe(() => {
        const sel = store.getState().selected;
        if (sel) recent.current = { id: sel.id, at: performance.now() };
      }),
    [store],
  );
  useEffect(() => {
    const ok = isWebGLAvailable();
    setWebgl(ok);
    layoutStore.dispatch({ type: "webgl", ok });
    return () => layoutStore.dispatch({ type: "webgl", ok: true });
  }, []);
  const locked = useNavSelector((n) => n.locked);
  return (
    <div
      className={`world-canvas${locked ? " is-locked" : ""}`}
      data-testid="world-canvas"
      onDoubleClick={() => {
        const nav = navStore.getState();
        const r = recent.current;
        if (nav.mode !== "orbit" || nav.inside || !r || performance.now() - r.at > 2000) return;
        if (navStore.entryOf(r.id).ok) void navStore.enter(r.id);
      }}
    >
      {webgl === false ? (
        <Fallback />
      ) : webgl ? (
        <>
          <SceneBoundary>
            <Suspense fallback={null}>
              <WorldScene store={store} />
            </Suspense>
          </SceneBoundary>
          <NavHud store={store} />
          <VehiclesUi />
          <LoadingScreen store={store} />
        </>
      ) : null}
      <Overlay store={store} />
    </div>
  );
}

export default WorldCanvas;
